import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that filters product cards based on search input.
 * Searches through cards with ref="cards[]" in the parent section and filters by product name.
 *
 * @typedef {object} CollectionSearchRefs
 * @property {HTMLInputElement} searchInput - The search input element.
 *
 * @extends Component<CollectionSearchRefs>
 */
export class CollectionSearchComponent extends Component {
  requiredRefs = ['searchInput'];

  /** @type {HTMLElement[]} */
  #cards = [];

  /** @type {HTMLElement | null} */
  #parentSection = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    // Find parent section
    this.#parentSection = this.closest('.shopify-section, section');

    if (!this.#parentSection) return;

    // Find all cards with ref="cards[]" in the section
    this.#updateCardsList();

    const { searchInput } = this.refs;

    if (searchInput instanceof HTMLInputElement) {
      this.#abortController = new AbortController();

      // Debounce search to avoid too many updates while typing
      const debouncedSearch = debounce(() => {
        this.#performSearch();
      }, 300);

      // Listen for input events
      searchInput.addEventListener('input', (event) => {
        event.stopPropagation();
        debouncedSearch();
      }, {
        signal: this.#abortController.signal,
      });

      // Also listen for change events (for mobile keyboards)
      searchInput.addEventListener('change', (event) => {
        event.stopPropagation();
        this.#performSearch();
      }, {
        signal: this.#abortController.signal,
      });

      // Initial search (in case there's a value on load)
      if (searchInput.value) {
        this.#performSearch();
      }
    }

    // Watch for new cards added to the section (for infinite scroll)
    this.#observeSection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }

    if (this.#sectionObserver) {
      this.#sectionObserver.disconnect();
      this.#sectionObserver = null;
    }
  }

  /**
   * Updates the list of cards by querying the section
   */
  #updateCardsList() {
    if (!this.#parentSection) return;

    // Find all elements with ref="cards[]" in the section
    this.#cards = Array.from(
      this.#parentSection.querySelectorAll('[ref="cards[]"]')
    );
  }

  /** @type {MutationObserver | null} */
  #sectionObserver = null;

  /**
   * Observes the section for new cards (for infinite scroll scenarios)
   */
  #observeSection() {
    if (!this.#parentSection) return;

    this.#sectionObserver = new MutationObserver(() => {
      // Update cards list when DOM changes
      const oldCardsCount = this.#cards.length;
      this.#updateCardsList();

      // If new cards were added, re-apply current search filter
      if (this.#cards.length > oldCardsCount) {
        this.#performSearch();
      }
    });

    this.#sectionObserver.observe(this.#parentSection, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Performs the search and filters cards
   */
  #performSearch() {
    const { searchInput } = this.refs;

    if (!(searchInput instanceof HTMLInputElement) || !this.#parentSection) return;

    const keyword = searchInput.value.trim().toLowerCase();

    // Update cards list in case new cards were added
    this.#updateCardsList();

    // Filter cards based on product name
    this.#cards.forEach((card) => {
      const productName = (card.getAttribute('data-product-name') || '').toLowerCase();
      const matches = keyword === '' || productName.includes(keyword);

      // Show or hide card based on match
      if (matches) {
        card.style.display = '';
        card.removeAttribute('hidden');
      } else {
        card.style.display = 'none';
        card.setAttribute('hidden', '');
      }
    });

    // Dispatch custom event for other components to listen
    this.dispatchEvent(
      new CustomEvent('collection-search', {
        bubbles: true,
        detail: {
          keyword,
          visibleCount: this.#cards.filter((card) => !card.hasAttribute('hidden')).length,
          totalCount: this.#cards.length,
        },
      })
    );
  }

  /**
   * Clears the search input and shows all cards
   */
  clearSearch() {
    const { searchInput } = this.refs;

    if (searchInput instanceof HTMLInputElement) {
      searchInput.value = '';
      this.#performSearch();
    }
  }
}

if (!customElements.get('collection-search-component')) {
  customElements.define('collection-search-component', CollectionSearchComponent);
}
